import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getRandomCharade, useGameStore } from '../store/gameStore';
import Timer from './Timer';
import PlayerToken from './PlayerToken';

interface CharadesProps {
  onComplete: (result: { type: 'success' | 'failure' | 'charades-success'; actorId?: string; guesserId?: string }) => void;
}

type CharadesPhase = 'warn' | 'ready' | 'playing' | 'selecting-guesser' | 'guesser-informed' | 'finished';

const Charades: React.FC<CharadesProps> = ({ onComplete }) => {
  const { players, currentPlayerIndex } = useGameStore();
  const [phase, setPhase] = useState<CharadesPhase>('warn');
  const [prompt, setPrompt] = useState('');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [guessedCorrectly, setGuessedCorrectly] = useState(false);
  const [selectedGuesserId, setSelectedGuesserId] = useState<string | null>(null);

  const currentPlayer = players[currentPlayerIndex];
  const otherPlayers = players.filter(p => p.id !== currentPlayer?.id);

  useEffect(() => {
    setPrompt(getRandomCharade());
  }, []);

  const handleRevealTopic = () => {
    setPhase('ready');
  };

  const startGame = () => {
    setPhase('playing');
    setIsTimerRunning(true);
  };

  const handleGuess = (correct: boolean) => {
    setIsTimerRunning(false);
    setGuessedCorrectly(correct);
    if (correct) {
      setPhase('selecting-guesser');
    } else {
      setPhase('finished');
      setTimeout(() => onComplete({ type: 'failure' }), 1500);
    }
  };

  const handleSelectGuesser = (guesserId: string) => {
    setSelectedGuesserId(guesserId);
    setPhase('guesser-informed');
    // After showing the info, close the modal and start sidebar rolling
    setTimeout(() => {
      onComplete({ type: 'charades-success', actorId: currentPlayer?.id, guesserId });
    }, 2000);
  };

  const handleTimeUp = () => {
    setIsTimerRunning(false);
    setGuessedCorrectly(false);
    setPhase('finished');
    setTimeout(() => onComplete({ type: 'failure' }), 1500);
  };

  // Dice face renderer
  const renderDiceFace = (num: number) => {
    const positions: Record<number, string[]> = {
      1: ['center'],
      2: ['top-right', 'bottom-left'],
      3: ['top-right', 'center', 'bottom-left'],
      4: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      5: ['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'],
      6: ['top-left', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-right'],
    };
    
    const dotPositions: Record<string, string> = {
      'top-left': 'top-2 left-2',
      'top-right': 'top-2 right-2',
      'middle-left': 'top-1/2 left-2 -translate-y-1/2',
      'middle-right': 'top-1/2 right-2 -translate-y-1/2',
      'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
      'bottom-left': 'bottom-2 left-2',
      'bottom-right': 'bottom-2 right-2',
    };
    
    return positions[num]?.map(pos => (
      <div key={pos} className={`absolute w-3 h-3 bg-black rounded-full ${dotPositions[pos]}`} />
    ));
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-br from-amber-900 to-orange-900 rounded-3xl p-8 max-w-lg w-full shadow-2xl border border-amber-500/30"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
      >
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-white mb-2">🎬 CHARADES!</h2>
          <p className="text-amber-300">Act it out! Others guess before time runs out!</p>
        </div>

        <AnimatePresence mode="wait">
          {phase === 'warn' && (
            <motion.div
              key="warn"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="bg-black/30 rounded-2xl p-6 mb-6">
                <motion.div
                  className="text-6xl mb-4"
                  animate={{ rotate: [0, -15, 15, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                >
                  👀
                </motion.div>
                <p className="text-xl text-white font-bold mb-2">LOOK AWAY!</p>
                <p className="text-amber-300 text-sm">Have all other players look away from the screen first!</p>
                <p className="text-amber-400/60 text-xs mt-2">Don't peek! The word will be revealed next.</p>
              </div>

              <motion.button
                onClick={handleRevealTopic}
                className="py-4 px-8 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-black font-bold text-xl shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                👁️ Reveal Topic
              </motion.button>
            </motion.div>
          )}

          {phase === 'ready' && (
            <motion.div
              key="ready"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="bg-black/30 rounded-2xl p-6 mb-6">
                <p className="text-amber-400 text-sm mb-2">YOUR WORD/PHRASE:</p>
                <p className="text-3xl font-bold text-white">{prompt}</p>
              </div>

              <motion.button
                onClick={startGame}
                className="py-4 px-8 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-black font-bold text-xl shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🎬 Start Acting!
              </motion.button>

              <p className="text-amber-300/60 text-sm mt-4">
                ⚠️ Don't say the word! You have 1 minute!
              </p>
            </motion.div>
          )}

          {phase === 'playing' && (
            <motion.div
              key="playing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="mb-6">
                <Timer
                  initialTime={60}
                  isRunning={isTimerRunning}
                  onComplete={handleTimeUp}
                />
              </div>

              <div className="bg-black/30 rounded-2xl p-4 mb-6">
                <p className="text-amber-300 text-sm mb-1">ACTING:</p>
                <p className="text-2xl font-bold text-white">{prompt}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  onClick={() => handleGuess(true)}
                  className="py-4 px-6 bg-gradient-to-br from-green-500 to-green-700 rounded-xl text-white font-bold text-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ✅ Guessed Right!
                </motion.button>
                <motion.button
                  onClick={() => handleGuess(false)}
                  className="py-4 px-6 bg-gradient-to-br from-red-500 to-red-700 rounded-xl text-white font-bold text-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ❌ No One Guessed
                </motion.button>
              </div>
            </motion.div>
          )}

          {phase === 'selecting-guesser' && (
            <motion.div
              key="selecting-guesser"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="bg-black/30 rounded-2xl p-6 mb-6">
                <motion.div
                  className="text-5xl mb-4"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: 2 }}
                >
                  🎉
                </motion.div>
                <p className="text-xl text-green-400 font-bold mb-2">CORRECT!</p>
                <p className="text-amber-300 text-sm">Select who guessed correctly:</p>
              </div>

              <div className="space-y-3 mb-6">
                {otherPlayers.map((player) => (
                  <motion.button
                    key={player.id}
                    onClick={() => handleSelectGuesser(player.id)}
                    className="w-full flex items-center gap-3 p-4 bg-black/30 rounded-xl border border-white/10 hover:border-amber-400/50 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <PlayerToken player={player} isActive={false} size="sm" />
                    <span className="text-white font-medium">{player.name}</span>
                    <span className="ml-auto text-amber-400 text-sm">Guessed it!</span>
                  </motion.button>
                ))}
              </div>

              <p className="text-amber-300/60 text-xs">
                Both {currentPlayer?.name} and the guesser will roll dice!
              </p>
            </motion.div>
          )}

          {phase === 'guesser-informed' && (
            <motion.div
              key="guesser-informed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="bg-black/30 rounded-2xl p-6 mb-6">
                <motion.div
                  className="text-5xl mb-4"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: 2 }}
                >
                  🎲
                </motion.div>
                <p className="text-xl text-green-400 font-bold mb-2">BOTH GET ONE ROLL!</p>
                <p className="text-amber-300 text-sm">
                  {currentPlayer?.name} and {players.find(p => p.id === selectedGuesserId)?.name} will each roll the dice on the sidebar!
                </p>
              </div>
              <motion.div
                className="text-gray-400 text-sm"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                Get ready to roll...
              </motion.div>
            </motion.div>
          )}

          {phase === 'finished' && (
            <motion.div
              key="finished"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              {guessedCorrectly ? (
                <>
                  <motion.div
                    className="text-6xl mb-4"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.5, repeat: 2 }}
                  >
                    🎉
                  </motion.div>
                  <p className="text-2xl font-bold text-green-400">CORRECT!</p>
                  <p className="text-green-300">Both players rolled and moved forward!</p>
                </>
              ) : (
                <>
                  <motion.div
                    className="text-6xl mb-4"
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 0.5, repeat: 2 }}
                  >
                    ⏰
                  </motion.div>
                  <p className="text-2xl font-bold text-red-400">TIME'S UP!</p>
                  <p className="text-red-300">Roll dice and move backward!</p>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default Charades;
